import { useQuery, gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { UserProfileFragment } from '../fragments/UserProfile';
import { ArtiklerArchiveSEOFragment } from '../fragments/artikler/ArtiklerArchiveSEOFragment';
import { OptimizedImageFragment } from '../fragments/artikler/ArtikkelSEOFragment';
import { filterArticlePosts, filterArticleCategories } from '../constants/categories';
import { buildSiteUrl } from '../lib/polylang';
import { createSchema, createBreadcrumbSchema } from '../lib/schema';
import {
  Header,
  Footer,
  Main,
  Container,
  NavigationMenu,
  SEO,
  SchemaMarkup,
} from '../components';
import ArticlesHero from '../components/ArticlesPage/ArticlesHero/ArticlesHero';
import ArticlesGrid from '../components/ArticlesPage/ArticlesGrid/ArticlesGrid';
import React, { useEffect, useState } from 'react';
import TopHero from '../components/skeletonloader/TopHero';
import classNames from 'classnames/bind';
import styles from '../styles/pages/Artikler.module.scss';

let cx = classNames.bind(styles);

export default function Artikler(props) {
  const [mounted, setMounted] = useState(false);
  const { data, loading = false, error = null } = { data: props.data || {} };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (error) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const viewer = data?.viewer;
  const page = data?.page;
  const archiveSEO = data?.contentType?.seo;

  // Filter posts to only include non-blog posts (articles)
  const articlePosts = filterArticlePosts(data?.posts?.nodes ?? []);
  
  // Filter categories to exclude blog categories
  const articleCategories = filterArticleCategories(data?.categories?.nodes ?? []);
  
  // Find the hero section from the flexible content
  const heroSection = page?.artikler?.artikkelside?.find(
    section => section.fieldGroupName === 'ArtiklerArtikkelsideHeroLayout'
  );

  const isLoading = loading || !mounted;

  // Get the page title and description from the hero section
  const pageTitle = heroSection?.mainHeaderText || 'Artikler';
  const pageDescription = heroSection?.subText || siteDescription;
  const seoDescription = archiveSEO?.description || page?.description || pageDescription || 'Les våre siste artikler om digital markedsføring, webdesign og teknologi.';

  return (
    <>
      <SEO 
        locale="nb_NO"
        hreflangAlternates={[
          { hrefLang: 'no', href: buildSiteUrl('/artikler') },
          { hrefLang: 'en', href: buildSiteUrl('/en/articles') },
          { hrefLang: 'x-default', href: buildSiteUrl('/artikler') },
        ]}
        seo={{
          title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Artikler - Mediabooster',
          description: seoDescription,
          canonicalUrl: buildSiteUrl('/artikler'),
          openGraph: {
            title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Artikler - Mediabooster',
            description: seoDescription,
            url: buildSiteUrl('/artikler'),
            siteName: siteTitle || 'Mediabooster',
            type: 'website',
            image: {
              url: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png',
              secureUrl: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png',
              width: 1200,
              height: 630,
              alt: 'Mediabooster - Digital markedsføring og webdesign'
            }
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: pageTitle ? `${pageTitle} - ${siteTitle || 'Mediabooster'}` : 'Artikler - Mediabooster',
            description: seoDescription,
            image: 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png'
          }
        }}
      />
      <SchemaMarkup
        schema={createSchema([
          createBreadcrumbSchema([
            { label: 'Hjem', path: '/' },
            { label: 'Artikler', path: '/artikler' },
          ]),
        ])}
      />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        viewer={viewer}
        sharedData={props.sharedData}
      />
      <Main>
        {isLoading ? (
          <TopHero />
        ) : (
          <>
            {heroSection && <ArticlesHero data={heroSection} />}
            <ArticlesGrid 
              categories={articleCategories} 
              posts={articlePosts}
            />
          </>
        )}
      </Main>
      <Footer 
        title={siteTitle} 
        menuItems={primaryMenu}
        settings={data}
        sharedData={props.sharedData}
      />
    </>
  );
}

Artikler.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${UserProfileFragment}
  ${ArtiklerArchiveSEOFragment}
  ${OptimizedImageFragment}
  query GetArtiklerPage(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
    $databaseId: ID!
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    ...UserProfileFragment
    contentType(id: "post", idType: NAME) {
      ...ArtiklerArchiveSEOFragment
    }
    page(id: $databaseId, idType: DATABASE_ID) {
      artikler {
        artikkelside {
          ... on ArtiklerArtikkelsideHeroLayout {
            fieldGroupName
            mainHeaderText
            smallTopHeader
            subText
            button {
              buttonLink {
                url
              }
              buttonText
            }
          }
        }
      }
    }
    categories(where: { language: NB }) {
      nodes {
        id
        name
        slug
        count
      }
    }
    posts(first: 500, where: { language: NB }) {
      nodes {
        id
        title
        slug
        excerpt
        uri
        categories {
          nodes {
            id
            name
            slug
          }
        }
        tags {
          nodes {
            name
            slug
          }
        }
        featuredImage {
          node {
            ...OptimizedImageFragment
          }
        }
      }
    }
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

Artikler.variables = () => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
    databaseId: "15806"
  };
};

// Helper query for paginated posts
const POSTS_PAGINATION_QUERY = gql`
  ${OptimizedImageFragment}
  query GetPaginatedPosts($first: Int!, $after: String) {
    posts(first: $first, after: $after, where: { language: NB }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        title
        slug
        excerpt
        uri
        categories {
          nodes {
            id
            name
            slug
          }
        }
        tags {
          nodes {
            name
            slug
          }
        }
        featuredImage {
          node {
            ...OptimizedImageFragment
          }
        }
      }
    }
  }
`;

// Add getStaticProps for server-side data fetching with shared data
export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data (without posts - we'll fetch those separately with pagination)
    const { data: pageData } = await apolloClient.query({
      query: Artikler.query,
      variables: Artikler.variables(),
      fetchPolicy: 'no-cache'
    });
    
    // Fetch ALL posts using pagination (WPGraphQL limits to 100 per request)
    let allPosts = [];
    let hasNextPage = true;
    let afterCursor = null;
    
    while (hasNextPage) {
      const { data: postsData } = await apolloClient.query({
        query: POSTS_PAGINATION_QUERY,
        variables: { first: 100, after: afterCursor },
        fetchPolicy: 'no-cache'
      });
      
      const newPosts = postsData?.posts?.nodes ?? [];
      allPosts = [...allPosts, ...newPosts];
      
      hasNextPage = postsData?.posts?.pageInfo?.hasNextPage ?? false;
      afterCursor = postsData?.posts?.pageInfo?.endCursor;
    }
    
    // Merge paginated posts into pageData
    const mergedPageData = {
      ...pageData,
      posts: {
        nodes: allPosts
      }
    };
    
    // Fetch shared data using exact same queries as components
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        query GetSharedArtiklerData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # MediaBooster settings - includes both Footer and ContactCTA data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              logo {
                node {
                  mediaItemUrl
                  altText
                }
              }
              logoIcon {
                node {
                  mediaItemUrl
                  altText
                }
              }
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
            }
          }
          
          # Frontpage tjenesteboks data used by Header
          page(id: "14", idType: DATABASE_ID) {
            frontPage {
              tjenesteboks {
                tjenestenavn
                underoverskrift
                link
              }
            }
          }

          employees: ansatte(first: 100, where: { language: NB }) {
            nodes {
              id
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: mergedPageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching artikler data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}